import { useCallback, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { activityStore } from '../../../../../store/activity.store';
import { themeStore } from '../../../../../store/theme.store';
import { ActivityService } from '../../../../../api/activity.service';
import { Settings } from '../../../../../model/auth.model';
import { APP_SETTINGS_LS_KEY } from '../../../../../constant/key';
import { renderChartsForPdf } from './chartRenderers';
import {
  addHeader,
  addCharts,
  addRecordsTable,
  addFooter
} from './pdfHelpers';
import { containsCJK, loadCJKFont } from './fontLoader';

const MAX_EXPORT_DAYS = 50;

export interface PdfExportAlert {
  open: boolean;
  title: string;
  message: string;
}

export const usePdfExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [alert, setAlert] = useState<PdfExportAlert>({ open: false, title: '', message: '' });
  const { formatMessage } = useIntl();

  const closeAlert = () => setAlert({ open: false, title: '', message: '' });

  const statistics = activityStore(state => state.statistics);
  const activityTypes = activityStore(state => state.activityTypes);
  const intervalsParams = activityStore(state => state.intervalsParams);
  const { from, to, groupBy } = intervalsParams;
  const isDark = themeStore(state => state.isDark);

  const [settings] = useLocalStorage<Settings | null>(APP_SETTINGS_LS_KEY, null);
  const grouped = settings?.displayGroupsInBarStatistics ?? false;

  const exportPdf = useCallback(async () => {
    if (!statistics || !activityTypes || !from || !to) {
      console.error('Missing required data for PDF export');
      return;
    }

    // Calculate number of days (dates are in YYYY-MM-DD format)
    const fromDate = dayjs(from);
    const toDate = dayjs(to);
    const daysDiff = toDate.diff(fromDate, 'day') + 1;

    // Check if range exceeds limit
    if (daysDiff > MAX_EXPORT_DAYS) {
      setAlert({
        open: true,
        title: formatMessage({ id: 'EXPORT_PDF_ERROR_TITLE' }),
        message: formatMessage(
          { id: 'EXPORT_PDF_LIMIT_EXCEEDED' },
          { limit: MAX_EXPORT_DAYS, days: daysDiff }
        )
      });
      return;
    }

    setIsExporting(true);

    try {
      // Fetch all intervals for the date range with larger page size
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const intervalsResponse = await ActivityService.getIntervals(
        { ...intervalsParams, timezone: userTimezone },
        { page: 0, size: MAX_EXPORT_DAYS }
      );
      const daysIntervals = intervalsResponse.data.content;

      if (!daysIntervals || daysIntervals.length === 0) {
        setAlert({
          open: true,
          title: formatMessage({ id: 'EXPORT_PDF_ERROR_TITLE' }),
          message: formatMessage({ id: 'EXPORT_PDF_NO_DATA' })
        });
        setIsExporting(false);
        return;
      }

      // Generate chart images
      const chartImages = await renderChartsForPdf(statistics, grouped, isDark);

      // Dynamically import jsPDF and jspdf-autotable (reduces initial bundle by ~400KB)
      const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable')
      ]);

      // Create PDF document
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Check if CJK font is needed and load it
      const hasCJK = activityTypes.some(t => containsCJK(t.name)) ||
        daysIntervals.some(d => d.intervals.some(i => i.comment && containsCJK(i.comment)));
      if (hasCJK) {
        await loadCJKFont(doc);
      }

      // Add content
      let currentY = addHeader(doc, 'Time Tracking Report', {
        from: from || '',
        to: to || ''
      });

      // Records table first
      currentY = addRecordsTable(doc, daysIntervals, activityTypes, currentY, autoTable);

      // Check if we need a new page for charts
      if (currentY > 180) {
        doc.addPage();
        currentY = 20;
      }

      addCharts(doc, chartImages.barChart, chartImages.pieChart, statistics, grouped, groupBy, currentY);

      // Add footer to all pages
      addFooter(doc);

      // Generate filename with date range
      const filename = `time-report-${from || 'start'}-to-${to || 'end'}.pdf`;
      doc.save(filename);

    } catch (error) {
      console.error('PDF export failed:', error);
    } finally {
      setIsExporting(false);
    }
  }, [statistics, activityTypes, intervalsParams, from, to, groupBy, grouped, isDark, formatMessage]);

  return { exportPdf, isExporting, alert, closeAlert };
};
